import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch, advanceAuthGeneration } from "../utils/apiFetch";
import { readDisplayUser, saveDisplayUser, clearDisplayUser, removeLegacyToken } from "../utils/authStorage";
import { clearVideoPlayers } from "../utils/videoPlayerCache.mjs";

const AuthContext = createContext(null);
const storedUser = readDisplayUser;

export const AuthProvider = ({ children }) => {
  const [userId, updateUserId] = useState(() => storedUser()?.id || null);
  const [userInfo, setUserInfo] = useState(() => storedUser());
  const [authLoading, setAuthLoading] = useState(true);
  const [sessionVerified, setSessionVerified] = useState(false);
  const generation = useRef(0);
  const currentUserId = useRef(userId);
  currentUserId.current = userId;
  const setUserId = useCallback(id => {
    if (id !== currentUserId.current) clearVideoPlayers();
    advanceAuthGeneration();
    generation.current++;
    updateUserId(id);
    setSessionVerified(Boolean(id));
    setAuthLoading(false);
  }, []);
  const clearUser = useCallback(() => {
    clearDisplayUser();
    setUserId(null);
    setUserInfo(null);
  }, [setUserId]);

  useEffect(() => {
    let disposed = false;
    const controller = new AbortController();
    removeLegacyToken();
    const restoreSession = async () => {
      const current = ++generation.current;
      try {
        const response = await apiFetch(`${process.env.API_URL}/api/auth/me`, { signal: controller.signal });
        if (!response.ok) return;
        const data = await response.json();
        if (disposed || current !== generation.current || !data.user?.id) return;
        saveDisplayUser(data.user);
        if (data.user.id !== currentUserId.current) clearVideoPlayers();
        updateUserId(data.user.id);
        setUserInfo(data.user);
        setSessionVerified(true);
        const info = await apiFetch(`${process.env.API_URL}/api/userInfo`, { signal: controller.signal });
        if (info.ok) {
          const details = await info.json();
          if (!disposed && current === generation.current) setUserInfo(details);
        }
      } catch { /* A temporary outage must not erase the display cache. */ }
      finally { if (!disposed && current === generation.current) setAuthLoading(false); }
    };
    const onStorage = event => {
      if (event.key !== "user" && event.key !== null) return;
      generation.current++;
      advanceAuthGeneration();
      let nextUser;
      try { nextUser = JSON.parse(event.newValue); } catch { nextUser = null; }
      // Same-account profile refreshes must not remount and clear the query client.
      if (!nextUser?.id || nextUser.id !== currentUserId.current) {
        clearVideoPlayers();
        setAuthLoading(Boolean(nextUser?.id));
        updateUserId(null);
        setUserInfo(null);
        setSessionVerified(false);
      }
      if (event.newValue) restoreSession();
      else setAuthLoading(false);
    };
    const onResume = () => {
      if (document.visibilityState === "visible") restoreSession();
    };
    window.addEventListener("auth:expired", clearUser);
    window.addEventListener("storage", onStorage);
    window.addEventListener("visibilitychange", onResume);
    window.addEventListener("online", onResume);
    restoreSession();
    return () => {
      disposed = true;
      controller.abort();
      window.removeEventListener("auth:expired", clearUser);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("visibilitychange", onResume);
      window.removeEventListener("online", onResume);
    };
  }, [clearUser]);

  const value = useMemo(() => ({ userId, setUserId, userInfo, setUserInfo, authLoading, sessionVerified }),
    [userId, setUserId, userInfo, authLoading, sessionVerified]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
